import AdvancedSearch from '@app/components/AdvancedSearch'
import BidirectionalSort from '@app/components/BidirectionalSort'
import InfiniteScrollTable from '@app/components/InfiniteScrollTable'
import SearchBar from '@app/components/SearchBar'
import TypeAheadList from '@app/components/TypeAheadList'
import { IAssignment } from '@app/models/Assignment'
import { ICompanyContact } from '@app/models/CompanyContact'
import { AssignmentPriority } from '@app/models/enums'
import {
  IGlobalCompanyContactsState,
  IGlobalConfigurationState,
} from '@app/store/modules/global'
import { bindFunctions } from '@app/utils/class'
import {
  formatCurrency,
  formatDate,
  formatNumber,
  humanize,
} from '@app/utils/formatting'
import { IconName } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as React from 'react'
import DocumentTitle from 'react-document-title'
import { Link } from 'react-router-dom'
import { RouteComponentProps } from 'react-router-dom'
import { Column } from 'react-virtualized'
import {
  Button,
  Col,
  FormGroup,
  Input,
  Label,
  Nav,
  NavItem,
  NavLink,
} from 'reactstrap'
import {
  actionCreators,
  AssignmentsScope,
  IAssignmentsListState,
} from '../modules/assignmentsList'
import { ICurrentUserState } from '@app/store/modules/account'

type ComponentProps = IAssignmentsListState &
  IGlobalCompanyContactsState &
  IGlobalConfigurationState &
  ICurrentUserState &
  RouteComponentProps<{}> &
  typeof actionCreators

export default class AssignmentsListPage extends React.PureComponent<ComponentProps> {
  private tableRef: React.RefObject<InfiniteScrollTable>

  constructor(props: ComponentProps) {
    super(props)

    bindFunctions(this)

    this.tableRef = React.createRef<InfiniteScrollTable>()
  }

  public render() {
    return (
      <DocumentTitle title='Assignments'>
        <div className='container d-flex flex-column h-100 pt-3'>
          <div>
            <div className='mb-2 d-flex flex-row justify-content-between'>
              <h3>Assignments</h3>
              {this.props.user && !this.props.user.permissions.isGuest && (
                <Link to='/assignment/add' className='align-self-center'>
                  <Button size='sm' color='primary'>
                    <FontAwesomeIcon icon={['fal', 'plus']} fixedWidth />
                    <span>Add Assignment</span>
                  </Button>
                </Link>
              )}
            </div>
            <SearchBar
              onChange={this._onChangeFilter}
              placeholder='Looking for something?'
              size='sm'
              value={this.props.filterText}
            />
            <AdvancedSearch className='mt-1' expand='md'>
              <Col md={4} xl={3}>
                <FormGroup>
                  <Label>Date</Label>
                  <Input
                    type='select'
                    bsSize='sm'
                    value={this.props.dueDate}
                    onChange={this._onChangeDueDate}
                  >
                    <option key='date1' value='All'>
                      All
                    </option>
                    {this.props.configuration.assignmentDueAdvancedOptions
                      .map((c, i) => (
                        <option key={`date-${i}`} value={c}>
                          {humanize(c)}
                        </option>
                      ))
                      .toJS()}
                  </Input>
                </FormGroup>
              </Col>
              <Col md={4} xl={3}>
                <FormGroup>
                  <Label>Sort By</Label>
                  <Input
                    type='select'
                    bsSize='sm'
                    value={this.props.sortOrder}
                    onChange={this._onChangeSortOrder}
                  >
                    {this.props.configuration.assignmentSortOptions
                      .map((c, i) => (
                        <option key={`sort-${i}`} value={c}>
                          {humanize(c)}
                        </option>
                      ))
                      .toJS()}
                  </Input>
                </FormGroup>
              </Col>
              <BidirectionalSort
                md={4}
                xl={3}
                orderBy={this.props.orderBy}
                changeOrdering={this._onChangeOrderBy}
              />
              <Col md={4} xl={3}>
                <FormGroup>
                  <Label>Status</Label>
                  <Input
                    type='select'
                    bsSize='sm'
                    value={this.props.status}
                    onChange={this._onChangeStatus}
                  >
                    {this.props.configuration.assignmentStatuses
                      .map((c, i) => (
                        <option key={`status-${i}`} value={c}>
                          {humanize(c)}
                        </option>
                      ))
                      .toJS()}
                  </Input>
                </FormGroup>
              </Col>
              <Col md={4} xl={3}>
                <FormGroup>
                  <Label>Assigned To</Label>
                  <TypeAheadList
                    bsSize='sm'
                    labelKey='name'
                    dataSource={
                      !this.props.companyContacts
                        ? null
                        : this.props.companyContacts.results.toArray()
                    }
                    onChange={this._onChangeAssignedTo}
                  />
                </FormGroup>
              </Col>
            </AdvancedSearch>
            {this.props.user && !this.props.user.permissions.isGuest && (
              <div className='mb-3 mt-5'>
                <Nav tabs>
                  <NavItem>
                    <NavLink
                      active={this.props.scope === AssignmentsScope.Mine}
                      onClick={() => this._onChangeScope(AssignmentsScope.Mine)}
                    >
                      {AssignmentsScope.Mine}
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      active={this.props.scope === AssignmentsScope.Created}
                      onClick={() =>
                        this._onChangeScope(AssignmentsScope.Created)
                      }
                    >
                      {AssignmentsScope.Created}
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      active={this.props.scope === AssignmentsScope.All}
                      onClick={() => this._onChangeScope(AssignmentsScope.All)}
                    >
                      {AssignmentsScope.All}
                    </NavLink>
                  </NavItem>
                </Nav>
              </div>
            )}
          </div>
          <div>
            <InfiniteScrollTable
              isLoading={this.props.isLoading}
              items={this.props.items}
              rowHeight={50}
              loadItems={this.props.loadAssignments}
              noRowsRenderer={this._noRowsRenderer}
              onRowClicked={this._onRowClicked}
              totalItems={this.props.totalItems}
              ref={this.tableRef}
            >
              <Column
                cellRenderer={({ rowData }) => {
                  const assignment = rowData as IAssignment
                  let priorityIconName: IconName
                  let iconClasses: string | undefined
                  priorityIconName = 'minus-circle'

                  switch (assignment.priority) {
                    case AssignmentPriority.High:
                      iconClasses = 'text-danger'
                      priorityIconName = 'exclamation-circle'
                      break

                    case AssignmentPriority.Low:
                      iconClasses = 'text-info'
                      priorityIconName = 'arrow-alt-circle-down'
                      break

                    default:
                      priorityIconName = 'minus-circle'
                      break
                  }

                  return (
                    <div
                      onClick={(e) => {
                        e.stopPropagation()
                        this.togglePriority(assignment.id, assignment.priority)
                      }}
                    >
                      <FontAwesomeIcon
                        className={`mr-2 ${iconClasses}`}
                        icon={['fal', priorityIconName]}
                      />
                    </div>
                  )
                }}
                dataKey='priority'
                width={30}
              />
              <Column
                cellRenderer={({ rowData }) => {
                  const assignment = rowData as IAssignment

                  if (assignment.isComplete) {
                    return (
                      <div
                        onClick={(e) => {
                          e.stopPropagation()
                          this.props.setAssignmentCompletion(
                            assignment.id,
                            false,
                          )
                        }}
                      >
                        <FontAwesomeIcon
                          style={{ color: 'green' }}
                          className={`mr-2`}
                          icon={['fas', 'check']}
                        />
                      </div>
                    )
                  } else {
                    return (
                      <div
                        onClick={(e) => {
                          e.stopPropagation()
                          this.props.setAssignmentCompletion(
                            assignment.id,
                            true,
                          )
                        }}
                      >
                        <FontAwesomeIcon
                          className={`mr-2`}
                          style={{ color: 'lightgray' }}
                          icon={['fas', 'check']}
                        />
                      </div>
                    )
                  }
                }}
                dataKey='completed'
                width={30}
              />
              <Column
                cellDataGetter={({ rowData }) => {
                  const assignment = rowData as IAssignment
                  return assignment.title !== '' ? assignment.title : '-'
                }}
                dataKey='title'
                flexGrow={1}
                label='Title'
                width={100}
              />
              <Column
                cellRenderer={({ rowData }) => {
                  const assignment = rowData as IAssignment
                  const company = assignment.company && (
                    <div>{assignment.company.name}</div>
                  )

                  if (assignment.contacts && assignment.contacts.size > 0) {
                    if (assignment.contacts.size > 1) {
                      return (
                        <div>
                          {company}
                          <div>
                            {`${assignment.contacts.first().name} + ${
                              assignment.contacts.size - 1
                            } more`}
                          </div>
                        </div>
                      )
                    }
                    return (
                      <div>
                        {company}
                        <div>{assignment.contacts.first().name}</div>
                      </div>
                    )
                  }
                }}
                dataKey='name'
                flexGrow={1}
                label='Contact'
                width={200}
              />
              <Column
                cellRenderer={({ rowData }) => {
                  const assignment = rowData as IAssignment

                  const label = (
                    <div>
                      <div>{assignment.label}</div>
                    </div>
                  )

                  if (assignment.lotSize && assignment.lotSize.lotSize > 0) {
                    return (
                      <div>
                        {label}
                        <div>
                          {`${formatNumber(assignment.lotSize.lotSize)} ${
                            assignment.lotSize.units
                          }
                        ${
                          assignment.dollarOpportunity > 0
                            ? '/ ' +
                              formatCurrency(assignment.dollarOpportunity, 2)
                            : ''
                        }`}
                        </div>
                      </div>
                    )
                  } else {
                    return <div>{label}</div>
                  }
                }}
                headerClassName='d-none d-md-block'
                className='d-none d-md-block'
                dataKey='information'
                flexGrow={1}
                label='Information'
                width={50}
              />
              <Column
                cellDataGetter={({ rowData }) => {
                  const assignment = rowData as IAssignment

                  return assignment.dueDate
                    ? formatDate(assignment.dueDate, 'MM/DD/YYYY')
                    : 'No Due Date'
                }}
                headerClassName='d-none d-md-block'
                className='d-none d-md-block'
                dataKey='dueDate'
                flexGrow={1}
                label='Due Date'
                width={50}
              />
            </InfiniteScrollTable>
          </div>
        </div>
      </DocumentTitle>
    )
  }

  private togglePriority(id: string, priority: AssignmentPriority) {
    const items = []

    switch (priority) {
      case AssignmentPriority.High:
        this.props.setAssignmentPriority(id, 'Low')
        break
      case AssignmentPriority.Medium:
        this.props.setAssignmentPriority(id, 'High')
        break
      default:
        this.props.setAssignmentPriority(id, 'Medium')
        break
    }
  }

  private _noRowsRenderer() {
    return <div>No assignments</div>
  }

  private _onRowClicked(item: IAssignment, index: number) {
    this.props.history.push(`/assignment/${item.id}`)
  }

  private _onChangeAssignedTo(newFilter: ICompanyContact) {
    this.props.setAssignedTo(newFilter.contactId)
    this._reloadData()
  }

  private _onChangeDueDate(event: React.ChangeEvent<HTMLInputElement>) {
    this.props.setDueDate(event.target.value)
    this._reloadData()
  }

  private _onChangeFilter(newFilter: string) {
    this.props.setFilterText(newFilter)
    this._reloadData()
  }

  private _onChangeScope(newScope: AssignmentsScope) {
    this.props.setScope(newScope)
    this._reloadData()
  }

  private _onChangeStatus(event: React.ChangeEvent<HTMLInputElement>) {
    this.props.setStatus(event.target.value)
    this._reloadData()
  }

  private _onChangeSortOrder(event: React.ChangeEvent<HTMLInputElement>) {
    this.props.setSortOrder(event.target.value)
    this._reloadData()
  }

  private _onChangeOrderBy(event: React.ChangeEvent<HTMLInputElement>) {
    this.props.setOrderBy(event.target.value)
    this._reloadData()
  }

  private _reloadData() {
    if (this.tableRef.current) {
      this.tableRef.current.forceReloadData()
    }
  }
}
